import { useState } from 'react';
import {
  Button,
  Card,
  Descriptions,
  Space,
  Tag,
  Typography,
  Table,
  Alert,
  Spin,
  Divider,
  Popconfirm,
} from 'antd';
import { ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  useRecallById,
  useAffectedCustomers,
  useActivateRecall,
  useCompleteRecall,
} from '@hooks/useRegulatory';
import type {
  RecallStatus,
  RecallUrgency,
  RecallBatch,
  AffectedCustomer,
} from '@/types/regulatory';
import { downloadRecallPdf } from '@/api/exportApi';
import { MD_RECALL_REASON_LABEL_MAP } from './recallLabels';

// Порожні дати лишаються «—»: dayjs(undefined) повернув би сьогоднішню дату
const mdFormatDate = (mdValue: string | null | undefined): string =>
  mdValue ? dayjs(mdValue).format('DD.MM.YYYY') : '—';

const MD_STATUS_COLOR_MAP: Record<RecallStatus, string> = {
  Draft: 'default',
  Active: 'red',
  Completed: 'green',
  Cancelled: 'default',
};

const MD_STATUS_LABEL_MAP: Record<RecallStatus, string> = {
  Draft: 'Чернетка',
  Active: 'Активний',
  Completed: 'Завершений',
  Cancelled: 'Скасований',
};

const MD_URGENCY_COLOR_MAP: Record<RecallUrgency, string> = {
  Voluntary: 'default',
  Class1: 'red',
  Class2: 'orange',
  Class3: 'blue',
};

const MD_URGENCY_LABEL_MAP: Record<RecallUrgency, string> = {
  Voluntary: 'Добровільний',
  Class1: 'Клас I',
  Class2: 'Клас II',
  Class3: 'Клас III',
};

interface RecallDetailPageProps {
  recallId: string;
  onBack: () => void;
}

export default function RecallDetailPage({
  recallId,
  onBack,
}: RecallDetailPageProps) {
  const [mdPdfLoading, setMdPdfLoading] = useState(false);
  const { data: mdDetail, isLoading: mdIsLoading } = useRecallById(recallId);

  const mdHandleDownloadPdf = async () => {
    setMdPdfLoading(true);
    try {
      await downloadRecallPdf(recallId);
    } finally {
      setMdPdfLoading(false);
    }
  };
  const { data: mdAffectedCustomers, isLoading: mdCustomersLoading } = useAffectedCustomers(recallId);
  const mdActivateMutation = useActivateRecall();
  const mdCompleteMutation = useCompleteRecall();

  if (mdIsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!mdDetail) {
    return <Alert type="error" message="Recall не знайдено" showIcon />;
  }

  const mdBatchColumns: ColumnsType<RecallBatch> = [
    {
      title: 'Номер партії',
      dataIndex: 'batchNumber',
      key: 'batchNumber',
    },
  ];

  const mdCustomerColumns: ColumnsType<AffectedCustomer> = [
    {
      title: 'Клієнт',
      dataIndex: 'customerName',
      key: 'customerName',
    },
    {
      title: 'Контакти',
      key: 'contacts',
      render: (_: unknown, mdRecord: AffectedCustomer) =>
        [mdRecord.contactPhone, mdRecord.contactEmail].filter(Boolean).join(', ') || '—',
    },
    {
      title: 'Замовлення',
      dataIndex: 'salesOrderNumber',
      key: 'salesOrderNumber',
      width: 150,
    },
    {
      title: 'Дата замовлення',
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 140,
      render: (mdValue: string) => mdFormatDate(mdValue),
    },
    {
      // Поки завжди null: відвантаження не фіксує партію й дату по клієнту
      title: 'Партія',
      dataIndex: 'batchNumber',
      key: 'batchNumber',
      width: 140,
      render: (mdValue: string | null | undefined) => mdValue || '—',
    },
    {
      title: 'Дата відвантаження',
      dataIndex: 'shipmentDate',
      key: 'shipmentDate',
      width: 160,
      render: (mdValue: string | null | undefined) => mdFormatDate(mdValue),
    },
    {
      title: 'Відвантажено',
      dataIndex: 'quantityShipped',
      key: 'quantityShipped',
      width: 120,
      align: 'right',
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>
          Назад до recalls
        </Button>
      </Space>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Recall {mdDetail.number}
          </Typography.Title>
          <Space>
            <Tag color={MD_STATUS_COLOR_MAP[mdDetail.status]}>
              {MD_STATUS_LABEL_MAP[mdDetail.status]}
            </Tag>
            <Tag color={MD_URGENCY_COLOR_MAP[mdDetail.recallUrgency]}>
              {MD_URGENCY_LABEL_MAP[mdDetail.recallUrgency] ?? mdDetail.recallUrgency}
            </Tag>
            <Button
              icon={<DownloadOutlined />}
              onClick={mdHandleDownloadPdf}
              loading={mdPdfLoading}
            >
              Завантажити акт PDF
            </Button>
          </Space>
        </div>

        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="Продукт">{mdDetail.productName}</Descriptions.Item>
          <Descriptions.Item label="Причина">
            {MD_RECALL_REASON_LABEL_MAP[mdDetail.recallReason] ?? mdDetail.recallReason}
          </Descriptions.Item>
          <Descriptions.Item label="Дата ініціювання">{mdFormatDate(mdDetail.initiatedDate)}</Descriptions.Item>
          <Descriptions.Item label="К-ть партій">{mdDetail.affectedBatches.length}</Descriptions.Item>
          <Descriptions.Item label="Повідомлення надіслано">{mdDetail.notificationsSent ? 'Так' : 'Ні'}</Descriptions.Item>
          <Descriptions.Item label="Дата завершення">{mdFormatDate(mdDetail.completedDate)}</Descriptions.Item>
        </Descriptions>

        {mdDetail.description && (
          <>
            <Divider orientation="left">Опис</Divider>
            <Typography.Paragraph>{mdDetail.description}</Typography.Paragraph>
          </>
        )}

        <Divider orientation="left">Партії</Divider>
        <Table<RecallBatch>
          columns={mdBatchColumns}
          dataSource={mdDetail.affectedBatches}
          rowKey="id"
          pagination={false}
          size="small"
        />

        <Divider orientation="left">Постраждалі клієнти</Divider>
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Перелік побудовано за виробом, а не за партіями recall"
          description="Система не фіксує, яку партію відвантажено якому клієнту: показано всіх клієнтів із відвантаженими замовленнями на цей виріб. Перевірте партії за накладними перед повідомленням."
        />
        <Table<AffectedCustomer>
          columns={mdCustomerColumns}
          dataSource={mdAffectedCustomers}
          // Сервер повертає рядок на кожен рядок замовлення — ні клієнт, ні номер замовлення не унікальні
          rowKey={(mdRecord) =>
            `${mdRecord.customerId}-${mdRecord.salesOrderNumber}-${mdAffectedCustomers?.indexOf(mdRecord) ?? 0}`}
          loading={mdCustomersLoading}
          pagination={false}
          size="small"
        />

        <Divider />
        <Space>
          {mdDetail.status === 'Draft' && (
            <Popconfirm
              title="Активувати recall?"
              description="Всі вказані партії будуть заблоковані"
              onConfirm={() => mdActivateMutation.mutate(recallId)}
              okText="Активувати"
              cancelText="Скасувати"
              okButtonProps={{ danger: true }}
            >
              <Button type="primary" danger loading={mdActivateMutation.isPending}>
                Активувати
              </Button>
            </Popconfirm>
          )}
          {mdDetail.status === 'Active' && (
            <Button
              type="primary"
              onClick={() => mdCompleteMutation.mutate(recallId)}
              loading={mdCompleteMutation.isPending}
            >
              Завершити
            </Button>
          )}
        </Space>
      </Card>
    </>
  );
}
